import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { HandCoins, PiggyBank, FileText, Landmark, QrCode, Wallet, Check, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { SubHeader } from "@/components/Brand";
import { api, errMsg, useApi } from "@/lib/api";
import { rp, fmtDate } from "@/lib/format";

const KINDS = [
  { id: "angsuran", icon: HandCoins, t: "Angsuran Pinjaman", d: "Bayar angsuran pinjaman aktif" },
  { id: "setoran", icon: PiggyBank, t: "Setoran Simpanan", d: "Simpanan wajib / sukarela" },
  { id: "biaya_admin", icon: FileText, t: "Biaya Administrasi", d: "Iuran, denda, dan biaya lainnya" },
];
const METHODS = [
  { id: "transfer_bank", icon: Landmark, t: "Transfer Bank", d: "Transfer ke rekening CU" },
  { id: "qris", icon: QrCode, t: "QRIS", d: "Scan QRIS resmi CU" },
  { id: "saldo", icon: Wallet, t: "Saldo Anggota", d: "Potong dari simpanan sukarela" },
];

const PayInfo = ({ method, s }) => {
  if (method === "saldo") return <p className="text-xs text-slate-600">Saldo sukarela dipotong setelah admin menyetujui.</p>;
  if (method === "qris") return s?.qris_image_url
    ? <img src={s.qris_image_url} alt="QRIS CU" className="w-48 mx-auto rounded-lg border" data-testid="qris-image" />
    : <p className="text-xs text-slate-600">QRIS belum diatur admin. Silakan hubungi kantor CU {s?.office_phone}.</p>;
  return s?.account_no ? (
    <div className="text-sm" data-testid="cu-bank-info">
      <div className="text-xs text-slate-500">Transfer ke rekening CU:</div>
      <b>{s.bank_name} • {s.account_no}</b><div className="text-xs">a.n. {s.account_name}</div>
    </div>
  ) : <p className="text-xs text-slate-600">Rekening CU belum diatur admin. Hubungi kantor CU {s?.office_phone}.</p>;
};

export default function Payments() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { data: loans } = useApi("/loans");
  const { data: settings } = useApi("/settings");
  const [kind, setKind] = useState(params.get("jenis") || "angsuran");
  const [method, setMethod] = useState("transfer_bank");
  const [amount, setAmount] = useState(0);
  const [stype, setStype] = useState("wajib");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const loan = loans?.find((l) => l.status === "aktif");

  useEffect(() => {
    if (kind === "angsuran" && loan) setAmount(Math.min(loan.monthly_installment, loan.remaining));
  }, [kind, loan]);

  const submit = async () => {
    if (kind === "angsuran" && !loan) return toast.error("Tidak ada pinjaman aktif");
    if (amount <= 0) return toast.error("Masukkan nominal");
    setBusy(true);
    try {
      await api.post("/requests", { kind, amount, method, loan_id: loan?.loan_id, savings_type: kind === "setoran" ? stype : null, reference, note });
      toast.success("Pembayaran dikirim, menunggu verifikasi admin");
      navigate("/app/transaksi");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <SubHeader title="Pembayaran" onBack={() => navigate("/app/transaksi")} />
      <div className="px-5 space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {KINDS.map(({ id, icon: Icon, t, d }) => (
            <button key={id} onClick={() => setKind(id)} data-testid={`pay-kind-${id}`}
              className={`w-full flex items-center gap-4 p-4 text-left border-b last:border-0 border-slate-100 transition-colors ${kind === id ? "bg-red-50" : "hover:bg-slate-50"}`}>
              <Icon className={`h-7 w-7 ${kind === id ? "text-red-600" : "text-slate-500"}`} />
              <div className="flex-1"><div className="font-semibold text-sm">{t}</div><div className="text-xs text-slate-500">{d}</div></div>
              <ChevronRight className="h-5 w-5 text-slate-400" />
            </button>
          ))}
        </div>

        {kind === "angsuran" && (loan ? (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex justify-between items-center" data-testid="bill-card">
            <div>
              <div className="font-semibold text-sm">Angsuran Pinjaman</div>
              <div className="text-xl font-bold text-red-600">{rp(loan.monthly_installment)}</div>
              <div className="text-xs text-slate-600">Jatuh Tempo: {fmtDate(loan.next_due)} • Sisa {rp(loan.remaining)}</div>
            </div>
            <div className="h-7 w-7 bg-red-600 rounded-md flex items-center justify-center"><Check className="h-5 w-5 text-white" /></div>
          </div>
        ) : <div className="text-sm text-slate-600 bg-white border rounded-2xl p-4" data-testid="no-bill">Tidak ada tagihan angsuran aktif.</div>)}

        {kind === "setoran" && (
          <div className="grid grid-cols-3 gap-2">
            {["pokok", "wajib", "sukarela"].map((t) => (
              <button key={t} onClick={() => setStype(t)} data-testid={`savings-type-${t}`}
                className={`py-2 rounded-lg text-sm font-semibold border capitalize ${stype === t ? "bg-blue-800 text-white border-blue-800" : "bg-white border-slate-200"}`}>{t}</button>
            ))}
          </div>
        )}

        <div>
          <label className="text-sm font-semibold">Nominal</label>
          <Input data-testid="pay-amount-input" inputMode="numeric" className="mt-1.5 h-12 font-bold" value={amount ? `Rp ${amount.toLocaleString("id-ID")}` : ""}
            onChange={(e) => setAmount(Number(e.target.value.replace(/\D/g, "")) || 0)} placeholder="Rp 0" />
        </div>

        <div>
          <h2 className="font-bold mb-2">Metode Pembayaran</h2>
          <div className="space-y-2">
            {METHODS.map(({ id, icon: Icon, t, d }) => (
              <button key={id} onClick={() => setMethod(id)} data-testid={`pay-method-${id}`}
                className={`w-full flex items-center gap-3 p-3.5 rounded-xl border text-left transition-colors ${method === id ? "border-red-400 bg-red-50" : "border-slate-200 bg-white"}`}>
                <span className={`h-4 w-4 rounded-full border-2 ${method === id ? "border-red-600 bg-red-600 ring-2 ring-white ring-inset" : "border-slate-300"}`} />
                <Icon className="h-5 w-5 text-slate-700" />
                <div><div className="text-sm font-semibold">{t}</div><div className="text-[11px] text-slate-500">{d}</div></div>
              </button>
            ))}
          </div>
          <div className="bg-slate-100 rounded-xl p-3 mt-2"><PayInfo method={method} s={settings} /></div>
        </div>

        {method !== "saldo" && (
          <Input data-testid="pay-reference-input" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="No. referensi / nama pengirim transfer" />
        )}
        <Input data-testid="pay-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan (opsional)" />

        <button onClick={submit} disabled={busy} data-testid="pay-submit-button"
          className="w-full flex justify-between items-center bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-semibold py-3.5 px-5 rounded-xl transition-colors">
          <span>{busy ? "Mengirim..." : "Bayar Sekarang"}</span><span>{rp(amount)}</span>
        </button>
        <p className="text-[11px] text-center text-slate-500">Pembayaran diverifikasi secara manual oleh tim admin CU.</p>
      </div>
    </div>
  );
}
